const homePage = {
  hero: {
    kicker: "Health research data · pseudonymization",
    titleLine1: "Researchers get the data.",
    titleLine2: "Nobody gets the identities.",
    body: "Health research data arrives full of direct identifiers — kennitölur, names, addresses, phone numbers. Researchers need the data; they do not need the identities. Nafnleyndarkerfi sits between the two: every identifier is replaced with a code that carries no information about the person, and the real values are encrypted under separate keys.",
    links: {
      howItWorks: "How the system works",
      login: "Log in",
    },
  },
  isolationFigure: {
    sourceRecordLabel: "Source record",
    directIdentifiers: "Direct identifiers, encrypted at rest",
    project1Label: "Project — Heart study",
    project2Label: "Project — Diabetes registry",
    subjectPrefix: "subject",
    caption:
      "The same person, from the same source file, in two projects. Inside a project the code is stable, so datasets can be joined and colleagues can discuss the same subject. Across projects the codes never match — the two projects cannot combine their data or discover that they share a participant.",
  },
  theRule: {
    kicker: "The rule that matters",
    title: "A pseudonym is stable inside a project, and different in every other project.",
    body: "This is deliberate: collaboration within an approved project, hard isolation between projects. Each project's codes come from its own cryptographic key, held in a dedicated key-management service.",
  },
  steps: {
    kicker: "From upload to analysis",
    title: "Four steps",
    items: {
      dataArrives: {
        title: "Data arrives",
        body: "A hospital, a registry, or a researcher uploads a CSV into a project. Nothing is stored yet.",
      },
      identifiersFound: {
        title: "Identifiers are found",
        body: "The system flags columns that hold personal data — and reads inside free-text columns, where a kennitala buried in a sentence is the leak nobody catches by hand.",
      },
      personConfirms: {
        title: "A person confirms",
        body: "The uploader reviews every finding and decides: replace the identifier, or drop the column so its raw contents are never stored at all.",
      },
      researchersWork: {
        title: "Researchers work with pseudonyms",
        body: "The dataset enters the catalogue carrying codes instead of identities. The originals are encrypted under separate keys.",
      },
    },
  },
  roles: {
    kicker: "Who uses it",
    title: "Four kinds of account",
    body: "Access follows project membership, not seniority. Everyone belongs to exactly one department, and nothing crosses between departments — health sciences and social sciences share the software and share nothing else.",
    items: {
      researchers: {
        title: "Researchers",
        body: "Browse the dataset catalogue, download what they are cleared for, request what they are not, and pseudonymize their own files.",
      },
      principalInvestigators: {
        title: "Principal investigators",
        body: "Run projects: invite members, set access policy, review data requests, upload data, and forward requests to external providers.",
      },
      departmentHeads: {
        title: "Department heads",
        body: "Oversight of every project, dataset, request, and user in their own department — deliberately without the ability to download research data.",
      },
      dataProviders: {
        title: "Data providers",
        body: "Hospital and registry staff who supply data. They see only the requests addressed to them and the upload form — never the catalogue or anyone's results.",
      },
    },
  },
  limits: {
    kicker: "Scope",
    title: "What the system does not do",
    items: {
      notAnonymization: {
        title: "It is not statistical anonymization.",
        body: "Pseudonymization protects identities, not small-cell disclosure. A dataset of three patients with a rare diagnosis is still sensitive, and your obligations for the data itself do not go away.",
      },
      notEthicsReplacement: {
        title: "It does not replace ethics approval or a data processing agreement.",
        body: "It is a technical control that helps you meet your obligations under your IRB approval and under GDPR — not a substitute for either.",
      },
      notAccessDecision: {
        title: "It does not decide who may see what.",
        body: "People do. Principal investigators and department heads set the policy; the system enforces it, records it, and makes it reviewable.",
      },
    },
  },
  nextSteps: {
    cards: {
      about: {
        title: "About & how to use it",
        footerLink: "Read the guide →",
        body: "Roles, getting started, the dataset catalogue, data requests, uploading data, and what to do when something looks wrong. No technical background assumed.",
      },
      security: {
        title: "Security",
        footerLink: "How data is protected →",
        body: "How keys are managed, how data is encrypted, how access is controlled, and what is logged. For anyone who needs confidence in the system before entrusting data to it.",
      },
      roadmap: {
        title: "Roadmap",
        footerLink: "Where things stand →",
        body: "What is built and working today, described as it actually stands rather than as we would like it to be.",
      },
    },
  },
  access: {
    title: "Accounts are by invitation",
    body: "You cannot sign yourself up. Your principal investigator invites you by email address. The invitation cannot be used by anyone else. Two-factor authentication is mandatory for every account.",
    writeToUsBefore: "Would your department or research unit like its own space in the system? Write to",
    writeToUsSubject: "New department in Nafnleyndarkerfi",
    login: "Log in",
  },
};

export default homePage;
